// App router — plan §4.0 ekran envanteri.
// Public route'lar (/, /forgot, /reset, /first-login) her state'te erişilebilir.
// Authed ise AppShell içinde korunmuş route'lar, değilse /(ve *) Login'e yönlenir.

import { useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { useAuthStore } from "./stores/auth-store";
import { Login } from "./pages/Login";
import { Dashboard } from "./pages/Dashboard";
import { AppShell } from "./components/layout/AppShell";
import { ClientShell } from "./components/layout/ClientShell";
import { ClientHome } from "./pages/ClientHome";
import { ClientUsers } from "./pages/portal/ClientUsers";
import { PortalKpiImportPage } from "./pages/portal/PortalKpiImportPage";
import { PortalKpiOzetPage } from "./pages/portal/PortalKpiOzetPage";
import { PortalKpiTablePage } from "./pages/portal/PortalKpiTablePage";
import { UsersList } from "./pages/admin/UsersList";
import { UserNew } from "./pages/admin/UserNew";
import { FormManager } from "./pages/admin/FormManager";
import { Settings } from "./pages/admin/Settings";
import { RolesList } from "./pages/admin/RolesList";
import { AdminBranding } from "./pages/admin/AdminBranding";
import { AdminPromptLibrary } from "./pages/admin/AdminPromptLibrary";
import { SectorPackages } from "./pages/admin/SectorPackages";
import { ClientsList } from "./pages/ClientsList";
import { ClientProfile } from "./pages/ClientProfile";
import { ClientEntities } from "./pages/ClientEntities";
import { KpiCatalogPage } from "./pages/kpi/KpiCatalogPage";
import { KpiDisclosurePage } from "./pages/kpi/KpiDisclosurePage";
import { KpiHedeflerPage } from "./pages/kpi/KpiHedeflerPage";
import { KpiImportPage } from "./pages/kpi/KpiImportPage";
import { KpiOzetPage } from "./pages/kpi/KpiOzetPage";
import { KpiPaketlerPage } from "./pages/kpi/KpiPaketlerPage";
import { KpiSetsPage } from "./pages/kpi/KpiSetsPage";
import { KpiTablePage } from "./pages/kpi/KpiTablePage";
import { ClientNew } from "./pages/ClientNew";
import { ProjectsList } from "./pages/ProjectsList";
import { ProjectNew } from "./pages/ProjectNew";
import { ProjectDetail } from "./pages/ProjectDetail";
import { ReportImport } from "./pages/admin/ReportImport";
import { FormFill } from "./pages/FormFill";
import { GenerateProgress } from "./pages/GenerateProgress";
import { Review } from "./pages/Review";
import { ArchiveList } from "./pages/ArchiveList";
import { ArchiveDetailPage } from "./pages/ArchiveDetail";
import { Forgot } from "./pages/Forgot";
import { ResetPassword } from "./pages/ResetPassword";
import { FirstLogin } from "./pages/FirstLogin";

/** `/portal/kpi` (bildirim linki, `?setId=` taşıyabilir) → Veri Girişi; query korunur. */
function PortalKpiIndexRedirect(): JSX.Element {
  const location = useLocation();
  return <Navigate to={`/portal/kpi/tablo${location.search}`} replace />;
}

export function App(): JSX.Element {
  const status = useAuthStore((s) => s.status);
  const user = useAuthStore((s) => s.user);
  const bootstrap = useAuthStore((s) => s.bootstrap);

  useEffect(() => {
    void bootstrap();
  }, [bootstrap]);

  if (status === "idle" || status === "loading") {
    return (
      <main
        className="min-h-screen flex items-center justify-center"
        style={{ background: "var(--color-bg)", color: "var(--color-text-2)" }}
      >
        <p style={{ fontFamily: "var(--font-sans)" }}>Yükleniyor…</p>
      </main>
    );
  }

  const authed = status === "authenticated" && user !== null;

  return (
    <BrowserRouter>
      <Routes>
        {/* Public — auth'suz erişilebilir. Login ayrıca /first-login ve /reset
            tokenla gelen davet/sıfırlama linklerini karşılar. */}
        <Route path="/forgot" element={<Forgot />} />
        <Route path="/reset" element={<ResetPassword />} />
        <Route path="/first-login" element={<FirstLogin />} />

        {authed && user?.userKind === "client" ? (
          // Müşteri Katılımcı Rolü (Task 8) — sade route seti: ClientHome + FormFill +
          // M16 T19 portal (/portal/kpi/* Veri Girişi / Özet / İçe Aktar, /portal/users)
          // (ClientShell içinde, SIDEBAR YOK — üst sekmeler). Diğer TÜM path'ler ClientHome'a
          // redirect — backend zaten CLIENT_FORBIDDEN ile korur (auth/client-gate.ts),
          // bu FE tarafı aynı sınırı route seviyesinde de somutlaştırır (URL yazarak
          // internal ekranlara "girmiş gibi" görünme yok). RK27: portal route'ları catch-all'dan ÖNCE.
          <>
            <Route element={<ClientShell />}>
              <Route index element={<ClientHome />} />
              <Route path="/projects/:id/form" element={<FormFill />} />
              <Route path="/portal/kpi" element={<PortalKpiIndexRedirect />} />
              <Route path="/portal/kpi/tablo" element={<PortalKpiTablePage />} />
              <Route path="/portal/kpi/ozet" element={<PortalKpiOzetPage />} />
              <Route path="/portal/kpi/import" element={<PortalKpiImportPage />} />
              <Route path="/portal/users" element={<ClientUsers />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : authed ? (
          <>
            <Route element={<AppShell />}>
              <Route index element={<Dashboard />} />
              <Route path="/clients" element={<ClientsList />} />
              <Route path="/clients/new" element={<ClientNew />} />
              {/* M16 KPI (RK20): /clients/kpi/* — AppShell isParentActive prefix'i için Müşteriler
                  altında; statik segment `/clients/:id`'den ÖNCE tanımlanır. */}
              <Route path="/clients/kpi" element={<Navigate to="/clients/kpi/ozet" replace />} />
              <Route path="/clients/kpi/ozet" element={<KpiOzetPage />} />
              <Route path="/clients/kpi/tablo" element={<KpiTablePage />} />
              <Route path="/clients/kpi/import" element={<KpiImportPage />} />
              <Route path="/clients/kpi/setler" element={<KpiSetsPage />} />
              <Route path="/clients/kpi/paketler" element={<KpiPaketlerPage />} />
              <Route path="/clients/kpi/katalog" element={<KpiCatalogPage />} />
              <Route path="/clients/kpi/hedefler" element={<KpiHedeflerPage />} />
              <Route path="/clients/kpi/disclosure" element={<KpiDisclosurePage />} />
              <Route path="/clients/:id" element={<ClientProfile />} />
              <Route path="/clients/:id/entities" element={<ClientEntities />} />
              <Route path="/projects" element={<ProjectsList />} />
              <Route path="/projects/new" element={<ProjectNew />} />
              {/* Geçmiş Rapor Aktarımı (RG7): statik segment `/projects/:id`'den ÖNCE
                  tanımlanır — React Router v7 rank'lemesi statik segmenti zaten üstün
                  tutar, sıra ayrıca okunurluk içindir. */}
              <Route path="/projects/import" element={<ReportImport />} />
              <Route path="/projects/:id" element={<ProjectDetail />} />
              <Route path="/projects/:id/form" element={<FormFill />} />
              <Route path="/projects/:id/generate/progress" element={<GenerateProgress />} />
              <Route path="/projects/:id/review" element={<Review />} />
              <Route path="/archive" element={<ArchiveList />} />
              <Route path="/archive/:id" element={<ArchiveDetailPage />} />
              <Route path="/admin/users" element={<UsersList />} />
              <Route path="/admin/users/new" element={<UserNew />} />
              <Route path="/admin/roles" element={<RolesList />} />
              <Route path="/admin/frameworks" element={<FormManager />} />
              <Route path="/admin/frameworks/sector-packages" element={<SectorPackages />} />
              <Route path="/admin/branding" element={<AdminBranding />} />
              <Route
                path="/admin/settings"
                element={<Navigate to="/admin/settings/llm" replace />}
              />
              <Route path="/admin/settings/prompt-library" element={<AdminPromptLibrary />} />
              <Route path="/admin/settings/:section" element={<Settings />} />
            </Route>
            <Route path="*" element={<Navigate to="/" replace />} />
          </>
        ) : (
          <Route path="*" element={<Login />} />
        )}
      </Routes>
    </BrowserRouter>
  );
}
